import { ArrayItemPropertyDef, SchemaField, ZitiConfigType } from './service-template.model';

export function extractFieldsFromSchema(configType: ZitiConfigType): SchemaField[] {
    const fields: SchemaField[] = [];

    if (!configType.schema || typeof configType.schema !== 'object') {
        return fields;
    }

    const schema = configType.schema as any;
    const properties = schema.properties || {};
    const required = schema.required || [];
    const schemaDependencies = schema.dependencies || {};
    const dependentRequired = schema.dependentRequired || {};

    const resolveRef = (ref: string): any => {
        if (!ref || !ref.startsWith('#/')) {
            return null;
        }
        const path = ref.substring(2).split('/');
        let current = schema;
        for (const segment of path) {
            current = current[segment];
            if (!current) return null;
        }
        return current;
    };

    const buildArrayField = (fieldPath: string, key: string, isRequired: boolean, items: any): SchemaField => {
        let itemType = items.type || 'string';
        let itemProperties: any = items.properties;
        let itemEnum: any = items.enum;

        if (items.$ref) {
            const resolved = resolveRef(items.$ref);
            if (resolved) {
                itemType = resolved.type || 'object';
                itemProperties = resolved.properties;
                if (resolved.enum) {
                    itemEnum = resolved.enum;
                }
            } else {
                itemType = 'object';
            }
        }

        const field: SchemaField = {
            path: fieldPath,
            label: formatFieldLabel(key),
            type: 'array',
            required: isRequired,
            selected: isRequired,
            arrayItemType: itemType,
            includeInForm: true,
        };

        if (itemEnum && Array.isArray(itemEnum)) {
            field.enumOptions = itemEnum;
        }

        if (itemType === 'object' && itemProperties) {
            field.arrayItemProperties = {};
            for (const [propKey, propValue] of Object.entries(itemProperties)) {
                const propDef = propValue as any;
                const resolvedProp = propDef.$ref ? resolveRef(propDef.$ref) || propDef : propDef;
                const entry: ArrayItemPropertyDef = { type: resolvedProp.type || 'string' };
                if (resolvedProp.enum && Array.isArray(resolvedProp.enum)) {
                    entry.enumOptions = resolvedProp.enum;
                }
                field.arrayItemProperties[propKey] = entry;
            }
        }

        return field;
    };

    const extractFields = (props: any, path = '', parentRequired = false) => {
        for (const [key, value] of Object.entries(props)) {
            const fieldPath = path ? `${path}.${key}` : key;
            const prop = value as any;
            const isRequired = parentRequired || required.includes(key);

            let mergedProp = prop;
            if (prop.allOf) {
                mergedProp = { ...prop };
                for (const subSchema of prop.allOf) {
                    if (subSchema.$ref) {
                        const resolved = resolveRef(subSchema.$ref);
                        if (resolved) {
                            Object.assign(mergedProp, resolved);
                        }
                    } else {
                        Object.assign(mergedProp, subSchema);
                    }
                }
            }

            const hasItems = mergedProp.items || prop.items;

            if (mergedProp.$ref && !mergedProp.type && !mergedProp.properties && !hasItems) {
                const resolved = resolveRef(mergedProp.$ref);
                if (resolved) {
                    mergedProp = { ...mergedProp, ...resolved };
                }
            }

            const hasItemsAfterRef = mergedProp.items || prop.items;
            const isArrayTypeAfterRef = mergedProp.type === 'array' || hasItemsAfterRef;

            if (mergedProp.type === 'object' && mergedProp.properties) {
                extractFields(mergedProp.properties, fieldPath, isRequired);
            } else if (isArrayTypeAfterRef && hasItemsAfterRef) {
                fields.push(buildArrayField(fieldPath, key, isRequired, hasItemsAfterRef));
            } else if (prop.oneOf || prop.anyOf) {
                const options = prop.oneOf || prop.anyOf;
                const arrayOption = options.find((opt: any) => opt.type === 'array');

                if (arrayOption && arrayOption.items) {
                    fields.push(buildArrayField(fieldPath, key, isRequired, arrayOption.items));
                } else {
                    const refOption = options.find((opt: any) => opt.$ref);
                    if (refOption) {
                        const resolved = resolveRef(refOption.$ref);
                        fields.push({
                            path: fieldPath,
                            label: formatFieldLabel(key),
                            type: resolved?.type || 'string',
                            required: isRequired,
                            selected: isRequired,
                            includeInForm: true,
                        });
                    } else {
                        const typeOption = options.find((opt: any) => opt.type && opt.type !== 'null');
                        fields.push({
                            path: fieldPath,
                            label: formatFieldLabel(key),
                            type: typeOption?.type || prop.type || 'string',
                            required: isRequired,
                            selected: isRequired,
                            includeInForm: true,
                        });
                    }
                }
            } else if (prop.$ref) {
                const resolved = resolveRef(prop.$ref);
                if (resolved) {
                    if (resolved.type === 'object' && resolved.properties) {
                        extractFields(resolved.properties, fieldPath, isRequired);
                    } else if (resolved.type === 'array' && resolved.items) {
                        fields.push(buildArrayField(fieldPath, key, isRequired, resolved.items));
                    } else {
                        fields.push({
                            path: fieldPath,
                            label: formatFieldLabel(key),
                            type: resolved.type || 'string',
                            required: isRequired,
                            selected: isRequired,
                            includeInForm: true,
                        });
                    }
                } else {
                    fields.push({
                        path: fieldPath,
                        label: formatFieldLabel(key),
                        type: 'string',
                        required: isRequired,
                        selected: isRequired,
                        includeInForm: true,
                    });
                }
            } else {
                const field: SchemaField = {
                    path: fieldPath,
                    label: formatFieldLabel(key),
                    type: prop.type || 'string',
                    required: isRequired,
                    selected: isRequired,
                    includeInForm: true,
                };
                if (prop.enum && Array.isArray(prop.enum)) {
                    field.enumOptions = prop.enum;
                }
                fields.push(field);
            }
        }
    };

    extractFields(properties);

    processSchemaDependencies(fields, schemaDependencies, dependentRequired);
    applyConditionalDependencies(fields, schema);

    return fields;
}

export function processSchemaDependencies(
    fields: SchemaField[],
    schemaDependencies: any,
    dependentRequired: any
): void {
    for (const [fieldPath, requiredFields] of Object.entries(dependentRequired)) {
        if (Array.isArray(requiredFields)) {
            linkDependency(fields, fieldPath, requiredFields as string[]);
        }
    }

    for (const [fieldPath, dependency] of Object.entries(schemaDependencies)) {
        if (Array.isArray(dependency)) {
            linkDependency(fields, fieldPath, dependency as string[]);
        }
    }
}

function linkDependency(fields: SchemaField[], fieldPath: string, requiredFields: string[]): void {
    const field = fields.find((f) => f.path === fieldPath);
    if (field) {
        if (!field.dependentOf) field.dependentOf = [];
        field.dependentOf.push(...requiredFields);
    }

    requiredFields.forEach((depPath: string) => {
        const depField = fields.find((f) => f.path === depPath);
        if (depField) {
            if (!depField.dependencies) depField.dependencies = [];
            if (!depField.dependencies.includes(fieldPath)) {
                depField.dependencies.push(fieldPath);
            }
        }
    });
}

export function applyConditionalDependencies(fields: SchemaField[], schema: any): void {
    const allOf = schema?.allOf;
    if (!Array.isArray(allOf)) return;

    for (const entry of allOf) {
        const ifClause = entry?.if;
        const thenRequired = entry?.then?.required;
        if (!ifClause?.properties || !Array.isArray(thenRequired)) continue;

        for (const [triggerKey, triggerSchema] of Object.entries<any>(ifClause.properties)) {
            const hasConst = triggerSchema && Object.prototype.hasOwnProperty.call(triggerSchema, 'const');
            const triggerIsRequired = Array.isArray(ifClause.required) && ifClause.required.includes(triggerKey);
            if (!hasConst || !triggerIsRequired) continue;

            linkDependency(fields, triggerKey, thenRequired as string[]);
        }
    }
}

// Multi-value fields (arrays, enum checkbox groups, array-of-object editors, port
// ranges — all represented as `type: 'array'`) render as wide/full-row cards, so
// pushing them to the end keeps the grid's simple single-value fields grouped
// together first. A stable partition, not a sort — relative order within each
// group is preserved.
export function orderFieldsForDisplay(fields: SchemaField[]): SchemaField[] {
    const simple = fields.filter((field) => field.type !== 'array');
    const multiValue = fields.filter((field) => field.type === 'array');
    return [...simple, ...multiValue];
}

export function formatFieldLabel(fieldName: string): string {
    return fieldName
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, (str) => str.toUpperCase())
        .trim();
}
